"use client";

import React, {
    useCallback,
    useEffect,
    useState,
    type AriaAttributes,
} from "react";
import type { Nullable } from "../../@types/generic";
import { cn } from "../../helpers/classNames";
import { pushDataLayer } from "../../helpers/dataLayer";
import { useIntersectionObserver } from "../../hooks/useIntersectionObserver";
import ClientOnly from "../ClientOnly";
import { Arrow } from "./arrow";
import styles from "./styles.module.scss";

export type SliderContext =
    | "topLocations"
    | "forecastNavigation"
    | "miamap"
    | "touristicRegionWeather";
type Props = {
    children: React.ReactNode;
    minHeight?: string;
    dataTracking?: string;
    className?: string;
    context?: SliderContext;
    role?: React.AriaRole;
    ariaLabel?: AriaAttributes["aria-label"];
};

export const Slider: React.FC<Props> = ({
    children,
    minHeight = "200px",
    dataTracking,
    className,
    context,
    role,
    ariaLabel,
}) => {
    const [sliderRef, setSliderRef] = useState<Nullable<HTMLDivElement>>(null);
    const [firstChildIntersecting, setFirstChildIntersecting] =
        useState<boolean>(false);
    const [lastChildIntersecting, setLastChildIntersecting] =
        useState<boolean>(false);

    const firstChildObserverRef = useIntersectionObserver(
        {
            root: sliderRef,
            threshold: 0.99,
            axis: "horizontal",
        },
        (entry) => setFirstChildIntersecting(entry.isIntersecting)
    );

    const lastChildObserverRef = useIntersectionObserver(
        {
            root: sliderRef,
            threshold: 0.99,
            axis: "horizontal",
        },
        (entry) => setLastChildIntersecting(entry.isIntersecting)
    );

    useEffect(() => {
        if (dataTracking && sliderRef) {
            const scrollEnd = (): void => {
                pushDataLayer({
                    type: "scrollEnd",
                    event: "slider_scroll_end",
                    slider: dataTracking,
                });
            };
            sliderRef.addEventListener("scrollend", scrollEnd);

            return (): void => {
                sliderRef.removeEventListener("scrollend", scrollEnd);
            };
        }
        return (): void => {};
    }, [dataTracking, sliderRef]);

    const setupRefs = useCallback(
        (ref: Nullable<HTMLDivElement>) => {
            setSliderRef(ref);

            if (context === undefined) {
                return;
            }

            firstChildObserverRef(ref?.firstElementChild ?? null);
            lastChildObserverRef(ref?.lastElementChild ?? null);
        },
        [firstChildObserverRef, lastChildObserverRef, context]
    );

    const stepWidth =
        context === "topLocations" || context === "miamap" ? 150 : 50;

    const onClickArrow = useCallback(
        (direction: "right" | "left"): void => {
            if (sliderRef) {
                sliderRef.scrollBy(
                    direction === "left" ? -stepWidth : stepWidth,
                    0
                );
            }
        },
        [sliderRef, stepWidth]
    );

    return (
        <div className={styles.wrapper} role={role} aria-label={ariaLabel}>
            {context && !firstChildIntersecting ? (
                <ClientOnly>
                    <Arrow
                        direction="left"
                        context={context}
                        onClickAction={() => onClickArrow("left")}
                    />
                </ClientOnly>
            ) : null}
            <div
                style={{ minHeight }}
                className={cn(styles.slider, className)}
                data-tracking-slider={dataTracking}
                ref={setupRefs}
            >
                {children}
            </div>
            {context && !lastChildIntersecting ? (
                <ClientOnly>
                    <Arrow
                        direction="right"
                        context={context}
                        onClickAction={() => onClickArrow("right")}
                    />
                </ClientOnly>
            ) : null}
        </div>
    );
};
